import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";

export default function Gallery({ images = [], isLoading }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openModal = (item, index) => {
    setSelectedMedia(item);
    setCurrentIndex(index);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setSelectedMedia(null);
    document.body.style.overflow = "unset";
  };

  const navigateModal = (direction) => {
    const newIndex =
      direction === "next"
        ? (currentIndex + 1) % images.length
        : (currentIndex - 1 + images.length) % images.length;
    setCurrentIndex(newIndex);
    setSelectedMedia(images[newIndex]);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedMedia) return;
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowLeft") navigateModal("prev");
      if (e.key === "ArrowRight") navigateModal("next");
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedMedia, currentIndex, images]);

  return (
    <section
      className="py-20 md:py-28 px-6 bg-white dark:bg-[#121212] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <h2
            className="text-black dark:text-white font-medium font-serif leading-tight mb-4"
            style={{
              fontSize: "clamp(32px, 5vw, 56px)",
            }}
          >
            Event Gallery
          </h2>
          <p className="text-[#666666] dark:text-[#A0A0A0] text-lg md:text-xl max-w-2xl mx-auto">
            See our rentals in action at unforgettable events
          </p>
        </div>

        {/* Loading Skeleton */}
        {isLoading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse bg-white dark:bg-[#1E1E1E] p-3 pb-12 shadow-lg rounded-sm aspect-[4/5]"
              >
                <div className="w-full aspect-square bg-[#E0E0E0] dark:bg-[#333333]" />
                <div className="h-4 bg-[#E0E0E0] dark:bg-[#333333] mt-4 w-3/4 mx-auto" />
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && images.length === 0 && (
          <div className="text-center py-20 bg-[#FAFAFA] dark:bg-[#1A1A1A] rounded-3xl border border-[#E9E9E9] dark:border-[#333333]">
            <div className="mb-6 opacity-20 dark:opacity-40">
              <svg
                width="80"
                height="80"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mx-auto text-[#62397f]"
              >
                <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                <circle cx="9" cy="9" r="2" />
                <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
              </svg>
            </div>
            <h3 className="font-serif text-2xl font-bold text-black dark:text-white mb-2">
              No Gallery Photos
            </h3>
            <p className="text-[#666666] dark:text-[#A0A0A0]">
              We're still collecting photos from our latest beautiful events.
            </p>
          </div>
        )}

        {/* Polaroid Grid */}
        {!isLoading && images.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {images.map((image, index) => (
              <div
                key={index}
                className="group relative"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  animation: `fadeInUp 0.6s ease-out ${index * 0.05}s both`,
                }}
              >
                {/* Polaroid Frame */}
                <div
                  className="bg-white dark:bg-[#F5F5F5] p-3 pb-12 shadow-[0_4px_12px_rgba(0,0,0,0.15)] dark:shadow-[0_4px_20px_rgba(0,0,0,0.4)] transition-all duration-300 cursor-pointer"
                  onClick={() => openModal(image, index)}
                  style={{
                    transform:
                      hoveredIndex === index
                        ? "rotate(0deg) scale(1.05)"
                        : `rotate(${(index % 3) - 1 * 3}deg)`,
                  }}
                >
                  {/* Image */}
                  <div className="relative aspect-square overflow-hidden bg-[#E9E9E9] dark:bg-[#333333]">
                    {image.type === "video" ? (
                      <div className="w-full h-full relative">
                        <video
                          src={image.src || image.url}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          muted
                          playsInline
                          onMouseOver={(e) => e.target.play()}
                          onMouseOut={(e) => {
                            e.target.pause();
                            e.target.currentTime = 0;
                          }}
                        />
                      </div>
                    ) : (
                      <img
                        src={image.src || image.url}
                        alt={image.caption || image.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    )}
                    {/* Video Play Button Overlay */}
                    {image.type === "video" && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors duration-300">
                        <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-200">
                          <Play
                            size={20}
                            className="text-[#62397f] ml-1"
                            fill="#62397f"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Caption */}
                  <div className="absolute bottom-3 left-3 right-3 text-center">
                    <p
                      className="text-sm text-[#333333] font-medium"
                      style={{
                        fontFamily: "'Indie Flower', cursive",
                      }}
                    >
                      {image.caption || image.title}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View All CTA */}
        <div className="mt-16 text-center">
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#62397f] hover:bg-[#7C3AED] text-white font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_8px_24px_rgba(139,92,246,0.25)]"
          >
            View Full Gallery
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.5 15L12.5 10L7.5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>
      </div>

      {/* Modal */}
      {selectedMedia && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
          onClick={closeModal}
        >
          {/* Close Button */}
          <button
            onClick={closeModal}
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 z-50"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateModal("prev");
                }}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 z-50"
                aria-label="Previous"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateModal("next");
                }}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 z-50"
                aria-label="Next"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Modal Content */}
          <div
            className="relative max-w-7xl w-full mx-6 max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Media Display */}
            <div className="bg-black rounded-2xl overflow-hidden">
              {selectedMedia.type === "video" ? (
                <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                  <video
                    src={selectedMedia.src || selectedMedia.url}
                    className="absolute inset-0 w-full h-full rounded-xl"
                    controls
                    autoPlay
                    playsInline
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <img
                  src={selectedMedia.src || selectedMedia.url}
                  alt={selectedMedia.caption || selectedMedia.title}
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
              )}
            </div>

            {/* Media Info */}
            <div className="mt-6 text-center">
              <h3
                className="text-white font-serif font-medium text-xl md:text-2xl mb-2"
              >
                {selectedMedia.caption || selectedMedia.title}
              </h3>
              <p className="text-white/80 text-sm md:text-base mb-2">
                {selectedMedia.description}
              </p>
            </div>

            {/* Counter */}
            {images.length > 1 && (
              <div className="mt-4 text-center">
                <p className="text-white/60 text-sm">
                  {currentIndex + 1} / {images.length}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Animation Keyframes */}
      <style jsx="true" global="true">{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
